import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LOG_LIST_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as radioFilterId } from '../../filters/radio-filter/RadioFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { listTooltip, percentConfig } from './funcs/IpmList';
import { DEFAULT_INVALID_VALUE, FORMATTER_TYPES, integerFormatter } from '../../../utilities/FormattingUtilities';
import {
    timeRangeFilterId,
    siteFilterId,
    accessPointFilterId,
    wirelessControllerFilterId,
    radioBandFilterId,
} from '../../filters';
import { metricDefFormatter } from '../../../utilities';

const title = t`Radio Clients & Power`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'radio',
        entityFields: ['name', 'bssidList', 'band', 'location'],
        metrics: ['channels', 'channelUtilization', 'clientConnectionCount', 'txPower'],
        renderAsEntity: true,
    },
};

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Radio`,
        sortable: true,
        mapping: 'radio.name',
        getTooltip: listTooltip('radio.name'),
        flex: 1,
        link: {
            target: '../radio-detail',
            mappedFilterParams: {
                [radioFilterId]: 'radio',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Sites`,
        sortable: false,
        mapper: (row) => {
            const sites = row.radio.sites || [];
            if (sites.length === 0) return DEFAULT_INVALID_VALUE;
            return sites.map((site) => site.name).join(', ');
        },
        flex: 1,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Location`,
        sortable: true,
        mapping: 'radio.location',
        flex: 1,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Channels`,
        sortable: true,
        sortProperty: 'channels',
        mapper: (row) => {
            const channels = row.channels || [];
            if (channels.length === 0) return DEFAULT_INVALID_VALUE;
            return channels.map((channel) => channel.channelNumber).join(', ');
        },
        width: 110,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Channel Utilization`,
        formatter: metricDefFormatter(percentConfig),
        sortable: true,
        tooltip: false,
        mapping: 'channelUtilization',
        align: 'center',
        width: 175,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Max Clients`,
        sortable: true,
        tooltip: false,
        mapping: 'clientConnectionCount',
        width: 125,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Max Tx Power`,
        formatter: (value, _row, csvConfig) => integerFormatter(value, { baseUnits: ' dBm', ...csvConfig }),
        csvFormatter: { type: FORMATTER_TYPES.INTEGER, baseUnits: 'dBm' },
        sortable: true,
        tooltip: false,
        mapping: 'txPower',
        width: 150,
    },
];

const sorters = [{ property: 'radio.name', direction: 'asc' }];

/**
 * NOTE: this is a Radio panel, but does not appear on the Radio Detail page - it's a custom dashboard
 * panel created primarily for use by USAA, but is in the panel catalog for anyone else who wants it.
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function RadioClientsPowerListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="radio.deleted"
        />
    );
}

RadioClientsPowerListPanel.propTypes = panelPropTypes;

RadioClientsPowerListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'RadiosClientsPower',
    catalogEntry: {
        category: PANEL_CATEGORIES.RADIO,
        priority: PANEL_PRIORITIES.SUBLIST,
        title,
        description: t`List of wireless access point (AP) radios, showing max clients and TX power`,
        previewImage: LOG_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [siteFilterId, accessPointFilterId, wirelessControllerFilterId, radioBandFilterId],
    },
})(RadioClientsPowerListPanel);
